import Phaser from 'phaser';
import { ITile, PipValue, GameEvents } from '../core/types';
import { DominoTile } from './DominoTile';
import { EventBus } from '../utils/EventBus';
import { TILE } from '../utils/Constants';

export class PlayerHand extends Phaser.GameObjects.Container {
  private tiles: DominoTile[] = [];
  private handData: ITile[] = [];

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
  }

  public setTiles(tiles: ITile[]) {
    this.clearHand();
    this.handData = [...tiles];
    
    this.handData.forEach((tileData) => {
      const tile = new DominoTile(this.scene, 0, 0, tileData, false);
      this.add(tile);
      this.tiles.push(tile);
      
      tile.on('pointerdown', () => {
        if (tile.isPlayable) {
          EventBus.emit(GameEvents.TilePlayRequested, tileData);
        } else {
          tile.animateShake();
          EventBus.emit(GameEvents.TileInvalid, tileData);
        }
      });
    });
    
    this.layoutTiles();
  }

  public layoutTiles() {
    const tW = TILE?.WIDTH || 50;
    const spacing = Math.min(tW + 10, this.scene.scale.width / (this.tiles.length || 1));
    const totalWidth = (this.tiles.length - 1) * spacing;
    const startX = -totalWidth / 2;
    
    this.tiles.forEach((tile, index) => {
      this.scene.tweens.add({
        targets: tile,
        x: startX + index * spacing,
        y: 0,
        duration: 300,
        ease: 'Power2'
      });
    });
  }

  public highlightPlayable(leftEnd: PipValue | null, rightEnd: PipValue | null) {
    this.tiles.forEach(tile => {
      const t = tile.tileData;
      let playable = false;
      if (leftEnd === null && rightEnd === null) {
        playable = true;
      } else {
        playable = (t.a === leftEnd || t.b === leftEnd || t.a === rightEnd || t.b === rightEnd);
      }
      tile.setPlayable(playable);
      if (!playable) {
        tile.setAlpha(0.7);
      } else {
        tile.setAlpha(1);
      }
    });
  }

  public clearHighlights() {
    this.tiles.forEach(tile => {
      tile.setPlayable(false);
      tile.setAlpha(1);
    });
  }

  public removeTile(tileData: ITile) {
    const index = this.tiles.findIndex(t => t.tileData.id === tileData.id);
    if (index !== -1) {
      const tile = this.tiles[index];
      this.tiles.splice(index, 1);
      this.handData = this.handData.filter(t => t.id !== tileData.id);
      
      this.scene.tweens.add({
        targets: tile,
        y: tile.y - 100,
        alpha: 0,
        duration: 300,
        onComplete: () => {
          tile.destroy();
        }
      });
      
      this.layoutTiles();
    }
  }

  public enableInteraction() {
    this.tiles.forEach(tile => tile.setInteractive());
  }

  public disableInteraction() {
    this.tiles.forEach(tile => tile.disableInteractive());
  }

  public dealAnimation(tiles: ITile[]) {
    this.setTiles(tiles);
    
    const deckX = 0;
    const deckY = -200;
    
    this.tiles.forEach((tile, index) => {
      const targetX = tile.x;
      const targetY = tile.y;
      tile.setPosition(deckX, deckY);
      tile.setAlpha(0);
      
      this.scene.tweens.add({
        targets: tile,
        x: targetX,
        y: targetY,
        alpha: 1,
        duration: 400,
        delay: index * 100,
        ease: 'Back.easeOut'
      });
    });
  }

  public getTileCount(): number {
    return this.tiles.length;
  }
  
  private clearHand() {
    this.tiles.forEach(t => t.destroy());
    this.tiles = [];
    this.handData = [];
  }
}
